import type { ITeam } from '../interfaces/ITeam';
import type { IGame } from '../interfaces/IGame';
import type { IGameRecords } from '../interfaces/IGameRecords';
import { RankMethod } from '../enums/rank-method';

export abstract class TeamBase implements ITeam {
  TeamNameObs: KnockoutObservable<string>;
  isInit: boolean = false;
  TeamAbbreviation: string;
  TeamID: number;
  Division: string;
  Conference: string;
  TeamName: string;
  TeamLogo?: string;
  RegionName: string;

  Wins: KnockoutObservableArray<IGame>;
  Losses: KnockoutObservableArray<IGame>;
  Ties: KnockoutObservableArray<IGame>;
  GameRecords: IGameRecords;

  Schedule: KnockoutObservable<Array<KnockoutObservable<IGame>>>;
  FakeObs: KnockoutObservable<number | undefined>;
  ConferenceRank: KnockoutObservable<number>;
  ConferenceCss: KnockoutComputed<string>;
  SOV: KnockoutObservable<number>;
  SOS: KnockoutObservable<number>;
  SOSObj: KnockoutObservable<{ numerator: number; denominator: number } | undefined>;
  SOVObj: KnockoutObservable<{ numerator: number; denominator: number } | undefined>;
  DraftOrderRankMethod: KnockoutObservable<RankMethod>;
  DraftOrderRankMethodText: KnockoutComputed<string>;
  RankMethod: KnockoutObservable<RankMethod>;
  RankMethodText: KnockoutComputed<string>;
  ConferenceRankText: KnockoutComputed<string>;
  Tiebreakers: KnockoutObservable<ITiebreakerRecord>;
  TeamChecked: boolean = false;
  DraftOrderNumber: KnockoutObservable<number>;
  IsReplaced: boolean;
  SOVFractional: KnockoutComputed<string>;
  SOSFractional: KnockoutComputed<string>;

  constructor(
    teamAbbreviation: string,
    regionName: string,
    teamName: string,
    conference: string,
    division: string,
    teamID: number
  ) {
    const self = this;

    self.DraftOrderNumber = ko.observable(0);
    self.TeamAbbreviation = teamAbbreviation;
    self.RegionName = regionName;
    self.TeamNameObs = ko.observable(teamName);
    self.TeamName = self.TeamNameObs();
    self.Division = division;
    self.Conference = conference;
    self.TeamID = teamID;
    self.DraftOrderRankMethod = ko.observable(null);
    self.SOS = ko.observable(0);
    self.SOV = ko.observable(0);
    self.Tiebreakers = ko.observable({
      HTH: [],
      Sweeps: [],
      DivHTH: [],
    });

    self.Wins = ko.observableArray<IGame>([]);
    self.Losses = ko.observableArray<IGame>([]);
    self.Ties = ko.observableArray<IGame>([]);
    self.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    self.FakeObs = ko.observable<number | undefined>();

    // Subclass must construct and assign GameRecords in its constructor
    // after calling super() (because recordOptions may depend on subclass state)
    self.GameRecords = self.createGameRecords();

    self.ConferenceRank = ko.observable<number>();
    self.RankMethod = ko.observable<RankMethod>(RankMethod.Unknown);

    self.RankMethodText = ko.pureComputed(() => self.createRankMethodText());
    self.DraftOrderRankMethodText = ko.pureComputed(() => {
      const m = self.DraftOrderRankMethod();
      if (m == null) {
        return '';
      }
      return self.createRankMethodText(m);
    });
    self.ConferenceCss = ko.pureComputed(() => self.createConferenceCss());

    self.SOSObj = ko.observable<{ numerator: number; denominator: number } | undefined>(undefined);
    self.SOVObj = ko.observable<{ numerator: number; denominator: number } | undefined>(undefined);

    self.SOVFractional = ko.computed(() => {
      const sov = self.SOVObj() as unknown as { Wins: number; Losses: number; Ties: number } | undefined;
      if (!sov) return '';
      const numerator = sov.Wins + sov.Ties * 0.5;
      const denominator = sov.Wins + sov.Ties + sov.Losses;
      if (denominator === 0) return '';
      return `${numerator} / ${denominator}`;
    });

    self.SOSFractional = ko.computed(() => {
      const sos = self.SOSObj() as unknown as { Wins: number; Losses: number; Ties: number } | undefined;
      if (!sos) return '';
      const numerator = sos.Wins + sos.Ties * 0.5;
      const denominator = sos.Wins + sos.Ties + sos.Losses;
      if (denominator === 0) return '';
      return `${numerator} / ${denominator}`;
    });

    self.ConferenceRankText = ko.pureComputed(() => {
      self.FakeObs();
      let rank = '';
      if (self.ConferenceRank()) {
        rank += self.ConferenceRank();
        if (
          self.RankMethod() === RankMethod.SOS ||
          self.RankMethod() === RankMethod.CoinFlip ||
          self.RankMethod() === RankMethod.SOV
        ) {
          rank += '*';
        }
      }
      return rank;
    });
  }

  /**
   * Subclass must implement — returns a sport-specific IGameRecords instance.
   * Called during base constructor, so do not access uninitialized subclass state here.
   */
  protected abstract createGameRecords(): IGameRecords;

  /** Override to return the appropriate CSS class for conference playoff position */
  protected createConferenceCss(): string {
    this.FakeObs();
    // Default: subclasses must override with sport-specific cutoffs
    return 'out-post-season';
  }

  /** Override to return localized rank method text */
  protected createRankMethodText(method?: RankMethod): string {
    switch (method ?? this.RankMethod()) {
      case RankMethod.AllPCT:
        return 'Pct vs League';
      case RankMethod.ConfPCT:
        return 'Pct vs Conference';
      case RankMethod.DivPCT:
        return 'Pct vs Div';
      case RankMethod.SOS:
        return 'Strength of Schedule';
      case RankMethod.SOV:
        return 'Strength of Victory';
      case RankMethod.DivTop_AllPCT:
        return 'Top of Division - Pct vs League';
      case RankMethod.DivTop_DivPCT:
        return 'Top of Division - Pct vs Div';
      case RankMethod.DivTop_ConfPCT:
        return 'Top of Division - Pct vs Conference';
      case RankMethod.DivTop_SOS:
        return 'Top of Division - Strength of Schedule';
      case RankMethod.DivTop_SOV:
        return 'Top of Division - Strength of Victory';
      case RankMethod.DivTop_HTH:
        return 'Top of Division - Head to Head';
      case RankMethod.HTH:
        return 'Head-to-Head Tiebreaker';
      case RankMethod.CG:
        return 'Common Games';
      case RankMethod.CoinFlip:
        return 'Coin Flip';
      case RankMethod.CommonGames:
        return 'Common Games';
      case RankMethod.CommonPct:
        return 'Common Pct';
      case RankMethod.DivTop_CommonPct:
        return 'Top of Division - Common Pct';
      default:
        return '';
    }
  }

  sosToString(): string {
    const sos = this.SOSObj() as unknown as { Wins: number; Losses: number; Ties: number } | undefined;
    if (!sos) return '';
    let retStr = `${sos.Wins}-${sos.Losses}`;
    if (sos.Ties) {
      retStr += `-${sos.Ties}`;
    }
    return retStr;
  }

  sovToString(): string {
    const sov = this.SOVObj() as unknown as { Wins: number; Losses: number; Ties: number } | undefined;
    if (!sov) return '';
    let retStr = `${sov.Wins}-${sov.Losses}`;
    if (sov.Ties) {
      retStr += `-${sov.Ties}`;
    }
    return retStr;
  }

  /** Subclasses should override and call super.initSubscribers() */
  initSubscribers(): void {
    // Base: no common subscribers; GameRecords.initSubscribers() called by subclass
  }

  /** Subclasses should override and call super.reset() */
  reset(): void {
    const emptyWLT = { Wins: 0, Losses: 0, Ties: 0 };
    // The legacy code stored { Wins, Losses, Ties } not { numerator, denominator }
    // Cast to any to maintain runtime compat with SOVFractional/SOSFractional
    (this.SOVObj as KnockoutObservable<unknown>)(emptyWLT);
    this.SOVObj.valueHasMutated();
    (this.SOSObj as KnockoutObservable<unknown>)(emptyWLT);
    this.SOSObj.valueHasMutated();
    this.SOV(0);
    this.SOS(0);
    this.Wins([]);
    this.Losses([]);
    this.Ties([]);
    this.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    this.Tiebreakers({
      HTH: [],
      Sweeps: [],
      DivHTH: [],
    });
    this.ConferenceRank(null);
  }

  /** Convenience method to get current win percentage (used by GameRecords.getOppPct) */
  Pct(): number {
    return this.GameRecords?.AllRecord?.Pct?.() ?? 0;
  }
}
